import { programTypes, targetDataTypes } from 'boot/globals';
import { i18n } from 'boot/i18n';
import { colors } from 'quasar';

const { brightness } = colors;

/**
 * Get the target data type for a target, falling back to program type
 * if not specified in the target.
 *
 * @param {Object} target
 * @param {Object} program
 * @return {Object} The target data type object.
 */
export function getTargetDataType(targetDataType, programType) {
  const dataType = targetDataType
        || programTypes[programType]?.targetDataType
        || 'discreteTrial';
  return targetDataTypes.find((t) => t.value === dataType);
} // getTargetDataType()

/**
 * Return the primary metric for a target, falling back to program or target data type default
 *
 * @param {Object} target
 * @param {Object} program
 * @returns {string} metric
 */
export function getTargetMetric(targetPrimaryMetric, targetDataType, programPrimaryMetric, programType) {
  const metric = targetPrimaryMetric
        || programPrimaryMetric
        || programTypes[programType]?.defaultMetric
        || null;

  // Check if the selected badge format is allowed for this target
  const targetDataTypeInfo = getTargetDataType(targetDataType, programType);
  if (!metric || !targetDataTypeInfo?.allowedMetrics?.includes(metric)) {
    return targetDataTypeInfo?.defaultMetric;
  }
  if (!targetPrimaryMetric && !targetDataTypeInfo?.passThroughMetrics?.includes(metric)) {
    // If the target doesn't have a specific metric set we will only allow pass-through metrics
    // (metrics that make sense for this target types, e.g. not percentCorrect for duration targets)
    // This eases the user experience as they don't have to manually change the metric if they
    // add a duration- or rate target to a mixed program.
    return targetDataTypeInfo?.defaultMetric;
  }
  return metric;
} // getTargetMetric()

const RATE_METRIC_SECONDS = { ratePerMinute: 60, ratePerHour: 60 * 60 };

/**
 * Format a rate in the unit of the given measurement metric.
 *
 * @param {number} successes Number of occurrences
 * @param {number} duration Observed duration in seconds
 * @param {string} metric Measurement metric, as returned by getTargetMetric()
 * @returns {string|null} The formatted rate, or null when the metric is not a rate
 */
export function formatRate(successes, duration, metric) {
  const seconds = RATE_METRIC_SECONDS[metric];
  if (!seconds) {
    return null;
  }
  if (!(duration > 0)) {
    return '-';
  }
  const val = Math.round(((successes * seconds) / duration) * 10) / 10;
  return i18n.t(`measurementMetrics.${metric}.format`, { val });
} // formatRate()

const DURATION_METRIC_KEYS = ['totalDuration', 'minDuration', 'maxDuration', 'avgDuration'];

/**
 * Return the key under which a target's graph settings are stored in target.progressSettings.
 *
 * The key is the id of the graph view TargetStatistics pre-selects for the target, not the
 * measurement metric - the two only coincide for duration targets. Anything reading saved target
 * graph settings (progress reports included) has to resolve it the same way TargetStatistics does,
 * or it silently falls back to defaults.
 *
 * @param {String} targetPrimaryMetric target.primaryMetric
 * @param {String} targetDataType target.dataType
 * @param {String} programPrimaryMetric program.primaryMetric
 * @param {String} programType program.type
 * @param {Boolean} isRatingScale Whether the target is scored on a rating scale prompt level template
 * @returns {String} progressSettings key
 */
export function getTargetProgressSettingsKey(
  targetPrimaryMetric,
  targetDataType,
  programPrimaryMetric,
  programType,
  isRatingScale = false,
) {
  if (isRatingScale) {
    return 'spAverageRating';
  }

  const metric = getTargetMetric(targetPrimaryMetric, targetDataType, programPrimaryMetric, programType);

  let dataType;
  if (programType === 'simple') {
    dataType = targetDataType;
  } else if (programType !== 'taskAnalysis') {
    // Targets of Task Analysis programs are graphed with the default settings
    dataType = programType;
  }

  switch (dataType) {
    case 'frequency':
      return ['percentCorrect', 'ratePerHour', 'ratePerMinute'].includes(metric) ? 'default' : 'frequency';
    case 'duration':
      // The only views named after metrics, so the target's own metric can select one
      return DURATION_METRIC_KEYS.includes(metric) ? metric : 'totalDuration';
    case 'taskAnalysis':
      return 'totalTask';
    default:
      // Rate targets land here too: their views are 'default' and 'frequencyObservations', and a
      // rate metric names neither, so the first one always wins
      return 'default';
  }
} // getTargetProgressSettingsKey()

/**
 * Whether the "hide if min. trials not met" graph option applies to a target. It only counts trials,
 * so it is meaningless for rate and duration targets and for any metric other than percent correct.
 *
 * @param {String} targetDataType target.dataType
 * @param {String} targetMetric Resolved measurement metric for the target
 * @returns {Boolean}
 */
export function isMinTrialsFilterApplicable(targetDataType, targetMetric) {
  return !['rate', 'duration'].includes(targetDataType) && targetMetric === 'percentCorrect';
} // isMinTrialsFilterApplicable()

export function getTargetType(targetData, programData = null) {
  return targetData?.dataType
    || programTypes[programData?.type]?.targetDataType
    || 'discreteTrial';
} // getTargetType()

export function getTextColor(bgColor) {
  return brightness(bgColor) < 128 ? 'white' : 'black';
} // getTextColor()

/**
 * Determines whether a target should be excluded from progress calculations based on its data type.
 * Handles "self-nesting" types (like 'interval') where the parent container is excluded
 * to avoid double counting, but its sub-targets (children) are allowed.
 * @param {Object} target - The target object to evaluate.
 * @param {Array<string>} excludedDataTypes - List of data types globally excluded from progress counts.
 * @param {Array<string>} selfNestingTypes - Types that function as both containers and units of work.
 * @returns {Boolean} - Returns true if the target should be rejected/excluded, false otherwise.
 */
export const shouldExcludeTargetByType = (target, excludedDataTypes, selfNestingTypes = ['interval']) => {
  const type = target?.dataType;

  // Handle types that act as both parent containers and sub-targets
  if (selfNestingTypes.includes(type)) {
    // Exclude only if it's a parent (no parentTarget ID present)
    return !target.parentTarget;
  }

  // Standard check against the excluded types list for all other targets
  return excludedDataTypes.includes(type);
};
